"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

const API = "https://erp-xe9u.onrender.com/api";

const formatCurrency = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

export default function TandMCostPanel({ projectId, tandmId, token, onLoad }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  useEffect(() => {
    if (!tandmId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(
          `${API}/time-and-material2/${projectId}/${tandmId}/co-cost-preview`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) {
          setData(res.data);
          if (onLoad) onLoad(res.data);
        }
      } catch (err) {
        if (!cancelled)
          setError(
            err.response?.data?.message || "Failed to load T&M cost data",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, tandmId, token]);

  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  if (!tandmId) return null;

  if (loading) {
    return (
      <div className="bg-gray-700 rounded-lg border border-teal-600/40 p-4 animate-pulse">
        <div className="h-4 bg-gray-600 rounded w-52 mb-3" />
        <div className="h-3 bg-gray-600 rounded w-72 mb-2" />
        <div className="h-3 bg-gray-600 rounded w-48" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-700 rounded-lg border border-red-500/40 p-4">
        <p className="text-sm text-red-400">
          Could not load T&M cost data: {error}
        </p>
      </div>
    );
  }

  if (!data) return null;

  const {
    groups = [],
    totals = {},
    tandmNumber,
    scopeOfWork,
    approvedEntryCount,
  } = data;

  return (
    <div className="bg-gray-700 rounded-lg border border-teal-600/40 overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 bg-teal-900/30 border-b border-teal-600/30">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="text-teal-400 hover:text-teal-300 flex-shrink-0"
          >
            {collapsed ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronUpIcon className="h-4 w-4" />
            )}
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-teal-300 whitespace-nowrap">
                Cost Base — T&M #{tandmNumber}
              </span>
              <span className="px-2 py-0.5 text-[10px] rounded-full bg-teal-800/60 text-teal-300 border border-teal-600/30 whitespace-nowrap">
                Read-only
              </span>
              {approvedEntryCount > 1 && (
                <span className="px-2 py-0.5 text-[10px] rounded-full bg-gray-700 text-gray-400 whitespace-nowrap">
                  {approvedEntryCount} approved batch
                  {approvedEntryCount !== 1 ? "es" : ""}
                </span>
              )}
            </div>
            {scopeOfWork && (
              <p className="text-xs text-teal-600 mt-0.5 truncate max-w-xs">
                {scopeOfWork}
              </p>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="flex items-center gap-5 flex-shrink-0 ml-4">
          <div className="text-right">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">
              Material
            </p>
            <p className="text-sm text-blue-400 font-medium tabular-nums">
              {formatCurrency(totals.materialCost)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">
              Labor
            </p>
            <p className="text-sm text-purple-400 font-medium tabular-nums">
              {formatCurrency(totals.laborCost)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">
              Total Cost
            </p>
            <p className="text-base text-teal-300 font-semibold tabular-nums">
              {formatCurrency(totals.totalCost)}
            </p>
          </div>
        </div>
      </div>

      {/* Panel body */}
      {!collapsed && (
        <div className="divide-y divide-gray-600/50">
          {groups.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              No cost items found in the linked T&M budget.
            </div>
          ) : (
            groups.map((group) => {
              const isGroupCollapsed = collapsedGroups.has(group.conditionName);
              const groupTotal =
                (group.materialTotal || 0) + (group.laborTotal || 0);

              return (
                <div key={group.conditionName}>
                  <div
                    className="flex items-center justify-between px-4 py-2 bg-gray-800/40 hover:bg-gray-800/60 cursor-pointer select-none"
                    onClick={() => toggleGroup(group.conditionName)}
                  >
                    <div className="flex items-center gap-2">
                      {isGroupCollapsed ? (
                        <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                      ) : (
                        <ChevronUpIcon className="h-3.5 w-3.5 text-gray-400" />
                      )}
                      <span className="text-xs font-medium text-gray-300">
                        {group.conditionName}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {group.items.length} item
                        {group.items.length !== 1 ? "s" : ""}
                      </span>
                      {group.materialTotal > 0 && (
                        <span className="text-[10px] text-blue-400">
                          {formatCurrency(group.materialTotal)} mat
                        </span>
                      )}
                      {group.laborTotal > 0 && (
                        <span className="text-[10px] text-purple-400">
                          {formatCurrency(group.laborTotal)} lab
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-teal-400 tabular-nums">
                      {formatCurrency(groupTotal)}
                    </span>
                  </div>

                  {!isGroupCollapsed && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-700/40">
                            <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase min-w-[160px]">
                              Description
                            </th>
                            <th className="px-3 py-1.5 text-center text-[10px] font-medium text-gray-500 uppercase">
                              Cat
                            </th>
                            <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">
                              Bid Area
                            </th>
                            <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">
                              Size
                            </th>
                            <th className="px-3 py-1.5 text-right text-[10px] font-medium text-gray-500 uppercase">
                              Unit Cost
                            </th>
                            <th className="px-3 py-1.5 text-right text-[10px] font-medium text-gray-500 uppercase">
                              Qty
                            </th>
                            <th className="px-3 py-1.5 text-right text-[10px] font-medium text-gray-500 uppercase">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                          {group.items.map((li) => (
                            <tr
                              key={String(li._id)}
                              className="hover:bg-gray-700/20"
                            >
                              <td className="px-3 py-1.5 text-gray-300">
                                {li.itemDescription || "—"}
                              </td>
                              <td className="px-3 py-1.5 text-center">
                                <span
                                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${li.category === "M" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}
                                >
                                  {li.category === "M" ? "Mat" : "Lab"}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-gray-400">
                                {li.bidArea || "—"}
                              </td>
                              <td className="px-3 py-1.5 text-gray-400 font-mono">
                                {li.size || "—"}
                              </td>
                              <td className="px-3 py-1.5 text-right text-gray-300">
                                {formatCurrency(li.unitCost)}
                              </td>
                              <td className="px-3 py-1.5 text-right text-gray-300">
                                {li.quantity}
                                {li.uom && (
                                  <span className="text-gray-500 ml-1">
                                    {li.uom}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-right font-medium text-gray-200">
                                {formatCurrency(li.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-gray-600/50 bg-gray-800/30">
                            <td
                              colSpan={6}
                              className="px-3 py-1.5 text-right text-[10px] text-gray-500"
                            >
                              Group total
                            </td>
                            <td className="px-3 py-1.5 text-right text-xs font-semibold text-teal-400 tabular-nums">
                              {formatCurrency(groupTotal)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
